import React, { useEffect, useMemo, useState } from 'react';
import { plannedSchedule, plannedDetails, plannedSegments } from './plannedSegments';
import PlannedSchedule from './PlannedSchedule';
import { useAuth } from '../../context/AuthContext';
import { canWrite } from '../../utils/roles';
import RRHHNativeShell from './RRHHNativeShell';
import { rrhhCoreGet, rrhhCorePost } from './rrhhCoreApi';
import JournalMarksEditor from './JournalMarksEditor';
import { analysisMonths, buildRestMatrix, emptyRestMonth, restDaysText } from './analysisRest';
import { currentIsoDate, formatHours, formatMoney, monthKey, monthRange, normalizeStatus } from './rrhhUtils';
import './RRHHNative.css';

const REPORT_TYPES = {
  nomina: 'Nómina por persona',
  marcas: 'Marcas de reloj',
  operaciones: 'Operaciones',
  observaciones: 'Observaciones',
  control: 'Planificado vs. real',
  documentacion: 'Documentación',
};

function dateFromOperation(row) { return String(row.fecha || row.fecha_hora || '').slice(0, 10); }
function personName(row) { return row.persona || row.nickname || row.first_name || `Persona ${row.persona_id || row.id}`; }
function fullPersonName(row) {
  const firstName = String(row.first_name || '').trim();
  const lastName = String(row.last_name || '').trim();
  const baseName = [firstName, lastName].filter(Boolean).join(' ').trim() || personName(row);
  const nickname = String(row.nickname || '').trim();
  if (!nickname || nickname.toLocaleLowerCase('es') === baseName.toLocaleLowerCase('es')) return baseName;
  return `${baseName} (${nickname})`;
}
function approved(state) { return ['APROBADA', 'VALIDADA_CON_INCIDENCIA'].includes(normalizeStatus(state)); }
function csvCell(value) { const text = String(value ?? '').replaceAll('"', '""'); return /[",\n]/.test(text) ? `"${text}"` : text; }
function isoDate(value) { return String(value || '').slice(0, 10); }

function hourlyRateForDate(snapshot, personnelId, date, fallback = 0) {
  const key = isoDate(date);
  const match = (snapshot.hourly_rates || []).find((rate) => {
    if (Number(rate.personnel_id) !== Number(personnelId)) return false;
    const from = isoDate(rate.valid_from);
    const to = isoDate(rate.valid_to);
    return from && from <= key && (!to || to >= key);
  });
  return Number(match?.hourly_rate ?? fallback ?? 0);
}

function downloadCsv(rows, filename) {
  if (!rows.length) return;
  const columns = Object.keys(rows[0]);
  const csv = [columns, ...rows.map((row) => columns.map((column) => row[column]))]
    .map((line) => line.map(csvCell).join(',')).join('\n');
  const link = document.createElement('a');
  link.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}

function periodFor(kind, selectedDay, selectedMonth) {
  if (kind === 'dashboard') return { from: selectedDay, to: selectedDay };
  return monthRange(selectedMonth);
}

function shiftIsoDate(value, days) {
  const date = new Date(`${value}T12:00:00`);
  date.setDate(date.getDate() + days);
  return date.toISOString().slice(0, 10);
}

function formatDashboardDateLabel(value) {
  const date = new Date(`${value}T12:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  const dayName = date.toLocaleDateString('es-UY', { weekday: 'long' });
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = String(date.getFullYear()).slice(-2);
  return `${dayName}, ${day}/${month}/${year}`;
}

function startOfMonth(value = currentIsoDate()) {
  const date = new Date(`${value}T12:00:00`);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-01`;
}

function endOfMonth(value = currentIsoDate()) {
  const date = new Date(`${value}T12:00:00`);
  return new Date(date.getFullYear(), date.getMonth() + 1, 0).toISOString().slice(0, 10);
}

function previousYearDate(value) {
  const date = new Date(`${value}T12:00:00`);
  date.setFullYear(date.getFullYear() - 1);
  return date.toISOString().slice(0, 10);
}

function previousWeekRange(reference = currentIsoDate()) {
  const date = new Date(`${reference}T12:00:00`);
  const day = date.getDay() || 7;
  date.setDate(date.getDate() - day - 6);
  const from = date.toISOString().slice(0, 10);
  date.setDate(date.getDate() + 6);
  return { from, to: date.toISOString().slice(0, 10) };
}

function analysisPresetRange(preset, reference = currentIsoDate()) {
  const today = reference;
  const date = new Date(`${reference}T12:00:00`);
  if (preset === 'previous-month') {
    date.setMonth(date.getMonth() - 1, 1);
    return monthRange(`${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`);
  }
  if (preset === 'previous-week') return previousWeekRange(reference);
  if (preset === 'first-half') return { from: startOfMonth(reference), to: `${reference.slice(0, 8)}15` };
  if (preset === 'second-half') return { from: `${reference.slice(0, 8)}16`, to: endOfMonth(reference) };
  return { from: startOfMonth(reference), to: today };
}

function periodLabel(from, to) {
  return `${formatDashboardDateLabel(from)} - ${formatDashboardDateLabel(to)}`;
}

function inclusiveDays(from, to) {
  const start = new Date(`${from}T12:00:00`);
  const end = new Date(`${to}T12:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return 0;
  return Math.max(1, Math.round((end - start) / 86400000) + 1);
}

function metric(label, value, note) {
  return <article className="rrhh-native-card rrhh-native-metric" key={label}><small>{label}</small><strong>{value}</strong>{note && <span className="text-sm text-dim">{note}</span>}</article>;
}

function formatSignedHours(value) {
  const number = Number(value || 0);
  if (!number) return '0';
  return `${number > 0 ? '+' : '-'}${formatHours(Math.abs(number))}`;
}

function stateBadge(state) {
  const normalized = normalizeStatus(state || 'PENDIENTE');
  const kind = normalized === 'APROBADA' ? 'badge-green' : ['RECHAZADA', 'NO_APROBADA'].includes(normalized) ? 'badge-red' : 'badge-yellow';
  return <span className={`badge ${kind}`}>{normalized.replaceAll('_', ' ')}</span>;
}

function minuteValue(date, time) {
  if (!date || !time) return null;
  const dateParts = String(date).split('-').map(Number);
  const timeParts = String(time).split(':').map(Number);
  if (dateParts.length !== 3 || timeParts.length < 2 || [...dateParts, ...timeParts].some((value) => !Number.isFinite(value))) return null;
  return Math.round(Date.UTC(dateParts[0], dateParts[1] - 1, dateParts[2], timeParts[0], timeParts[1]) / 60000);
}

function markDifference(row, markDate, markTime, side, compare) {
  const plannedTime = side === 'entrada' ? row.hora_inicio : row.hora_fin;
  if (!compare || !plannedTime || !markDate || !markTime || String(row.estado_turno).toUpperCase() !== 'NORMAL') {
    return { label: compare ? 'Sin comparación' : 'Tramo intermedio', tone: 'neutral', minutes: null };
  }
  let plannedDate = row.fecha;
  const lastPlanned = plannedSegments(row).at(-1);
  if (side === 'salida' && (lastPlanned ? lastPlanned.hora_fin < lastPlanned.hora_inicio : row.hora_fin < row.hora_inicio)) {
    const date = new Date(`${row.fecha}T12:00:00`);
    date.setDate(date.getDate() + 1);
    plannedDate = date.toISOString().slice(0, 10);
  }
  const actual = minuteValue(markDate, markTime);
  const planned = minuteValue(plannedDate, plannedTime);
  if (actual === null || planned === null) return { label: 'Sin comparación', tone: 'neutral', minutes: null };
  const difference = actual - planned;
  const absoluteDifference = Math.abs(difference);
  const tone = absoluteDifference <= 15 ? 'ok' : absoluteDifference <= 30 ? 'warning' : 'critical';
  return {
    label: difference === 0 ? 'En hora' : `${difference > 0 ? '+' : ''}${difference} min`,
    tone,
    minutes: difference,
  };
}

function locationValue(label, latitude, longitude) {
  const lat = Number(latitude);
  const lon = Number(longitude);
  const hasCoordinates = latitude !== null && latitude !== undefined && latitude !== ''
    && longitude !== null && longitude !== undefined && longitude !== ''
    && Number.isFinite(lat) && Number.isFinite(lon);
  const cleanLabel = String(label || '').trim();
  const isGeneric = ['Ubicación registrada', 'Ubicacion registrada', 'Ubicación pendiente', 'Ubicacion pendiente'].includes(cleanLabel);
  if (cleanLabel && !isGeneric) {
    return <span className="rrhh-daily-location" title={cleanLabel}>📍 {cleanLabel}</span>;
  }
  if (hasCoordinates) {
    const coordinates = `${lat.toFixed(5)}, ${lon.toFixed(5)}`;
    return <a className="rrhh-daily-location" href={`https://www.google.com/maps?q=${lat},${lon}`} target="_blank" rel="noreferrer" title={coordinates}>📍 {coordinates}</a>;
  }
  return <span>Sin ubicación</span>;
}

function journalDifferenceMinutes(row) {
  if (String(row.estado_turno).toUpperCase() !== 'NORMAL' || !row.hora_inicio || !row.hora_fin) return null;
  const differenceHours = Number(row.horas_trabajadas || 0) - Number(row.horas_previstas || 0);
  if (!Number.isFinite(differenceHours)) return null;
  return Math.round(differenceHours * 60);
}

function formatDifferenceMinutes(minutes) {
  if (!Number.isFinite(minutes)) return 'Sin comparación';
  const sign = minutes > 0 ? '+' : minutes < 0 ? '-' : '';
  const absolute = Math.abs(minutes);
  if (absolute < 60) return `${sign}${absolute} min`;
  const hours = absolute / 60;
  return `${sign}${hours.toLocaleString('es-UY', { minimumFractionDigits: 1, maximumFractionDigits: 2 })} h`;
}

function percent(value, total) {
  if (!total) return '0%';
  return `${Math.round((Number(value || 0) / Number(total || 0)) * 100)}%`;
}

function formatPercentDelta(value, previous) {
  if (!previous) return 'Sin base 2025';
  const delta = ((value - previous) / previous) * 100;
  return `${delta > 0 ? '+' : ''}${delta.toLocaleString('es-UY', { maximumFractionDigits: 1 })}% vs 2025`;
}

function compareValues(left, right, direction = 'desc') {
  const multiplier = direction === 'asc' ? 1 : -1;
  const leftNumber = typeof left === 'number' ? left : Number(left);
  const rightNumber = typeof right === 'number' ? right : Number(right);
  const leftIsNumber = Number.isFinite(leftNumber) && String(left ?? '').trim() !== '';
  const rightIsNumber = Number.isFinite(rightNumber) && String(right ?? '').trim() !== '';
  if (leftIsNumber || rightIsNumber) {
    if (!leftIsNumber) return 1;
    if (!rightIsNumber) return -1;
    return (leftNumber - rightNumber) * multiplier;
  }
  return String(left || '').localeCompare(String(right || ''), 'es') * multiplier;
}

const DASHBOARD_FILTERS_STORAGE_KEY = 'rrhh.dashboard.filters.v1';
const DASHBOARD_FILTER_DEFAULTS = {
  query: '',
  arrivalFilter: 'all',
  marksFilter: 'all',
  differenceFilter: 'all',
  pendingOnly: false,
  sortMode: 'none',
};

function readDashboardFilters() {
  if (typeof window === 'undefined') return DASHBOARD_FILTER_DEFAULTS;
  try {
    const saved = JSON.parse(window.localStorage.getItem(DASHBOARD_FILTERS_STORAGE_KEY) || '{}');
    return { ...DASHBOARD_FILTER_DEFAULTS, ...saved };
  } catch {
    return DASHBOARD_FILTER_DEFAULTS;
  }
}

function firstEntryDifferenceMinutes(row) {
  const segment = (row.tramos || []).find((item) => item.entrada_hora);
  if (!segment) return null;
  return markDifference(row, segment.entrada_fecha, segment.entrada_hora, 'entrada', true).minutes;
}

function compareOptionalNumbers(left, right, direction = 'asc') {
  const leftNumber = Number.isFinite(left) ? left : null;
  const rightNumber = Number.isFinite(right) ? right : null;
  if (leftNumber === null && rightNumber === null) return 0;
  if (leftNumber === null) return 1;
  if (rightNumber === null) return -1;
  return direction === 'desc' ? rightNumber - leftNumber : leftNumber - rightNumber;
}

function Dashboard({ snapshot, canEdit, onEdit, onJournalAction, savingKey }) {
  const journals = snapshot.journals || [];
  const operations = snapshot.operations || [];
  const observations = snapshot.observations || [];
  const [filters, setFilters] = useState(readDashboardFilters);
  const { query, arrivalFilter, marksFilter, differenceFilter, pendingOnly, sortMode } = filters;
  const planned = journals.reduce((sum, row) => sum + Number(row.horas_previstas || 0), 0);
  const worked = journals.reduce((sum, row) => sum + Number(row.horas_trabajadas || 0), 0);
  const pendingJournals = journals.filter((row) => !approved(row.estado_aprobacion)).length;
  const pendingOperations = operations.filter((row) => normalizeStatus(row.estado) === 'PENDIENTE').length;

  useEffect(() => {
    try {
      window.localStorage.setItem(DASHBOARD_FILTERS_STORAGE_KEY, JSON.stringify(filters));
    } catch {
      // Si el navegador bloquea storage, el dashboard conserva los filtros en memoria.
    }
  }, [filters]);

  const updateFilters = (patch) => setFilters((current) => ({ ...current, ...patch }));

  const filteredJournals = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase('es');
    const rows = journals.filter((row) => {
      const segments = row.tramos || [];
      const firstEntryIndex = segments.findIndex((segment) => segment.entrada_hora);
      const firstEntry = firstEntryIndex >= 0 ? segments[firstEntryIndex] : null;
      const arrivalTone = firstEntry
        ? markDifference(row, firstEntry.entrada_fecha, firstEntry.entrada_hora, 'entrada', true).tone
        : 'neutral';
      const hasMarks = (row.marcas_ids || []).length > 0
        || segments.some((segment) => segment.entrada_hora || segment.salida_hora);
      const differenceMinutes = journalDifferenceMinutes(row);
      const searchableText = [
        personName(row),
        row.rol_operativo,
        row.actividad_ubicacion,
        row.estado_turno,
        row.hora_inicio,
        row.hora_fin,
      ].filter(Boolean).join(' ').toLocaleLowerCase('es');
      if (normalizedQuery && !searchableText.includes(normalizedQuery)) return false;
      if (arrivalFilter !== 'all' && arrivalTone !== arrivalFilter) return false;
      if (marksFilter === 'with' && !hasMarks) return false;
      if (marksFilter === 'without' && hasMarks) return false;
      if (differenceFilter === 'negative' && !(differenceMinutes < 0)) return false;
      if (differenceFilter === 'positive' && !(differenceMinutes > 0)) return false;
      if (pendingOnly && approved(row.estado_aprobacion)) return false;
      return true;
    });
    if (sortMode === 'arrival') {
      rows.sort((left, right) => (
        compareOptionalNumbers(firstEntryDifferenceMinutes(left), firstEntryDifferenceMinutes(right))
        || personName(left).localeCompare(personName(right), 'es')
      ));
    }
    if (sortMode === 'hours') {
      rows.sort((left, right) => (
        compareOptionalNumbers(journalDifferenceMinutes(left), journalDifferenceMinutes(right), 'desc')
        || personName(left).localeCompare(personName(right), 'es')
      ));
    }
    return rows;
  }, [arrivalFilter, differenceFilter, journals, marksFilter, pendingOnly, query, sortMode]);
  return <>
    <section className="rrhh-native-grid">
      {metric('Jornadas previstas', `${formatHours(planned)} h`, `${journals.length} personas con registro`)}
      {metric('Horas reales', `${formatHours(worked)} h`, `${Math.round((worked - planned) * 100) / 100} h de diferencia`)}
      {metric('Jornales pendientes', pendingJournals, pendingJournals ? 'Requieren validación' : 'Al día')}
      {metric('Operaciones pendientes', pendingOperations, `${operations.length} enviadas desde reloj`)}
      {metric('Observaciones', observations.length, observations.filter((row) => !row.resuelta).length ? 'Hay pendientes de resolución' : 'Sin pendientes')}
    </section>
    <section className="rrhh-native-card rrhh-daily-control">
      <header className="rrhh-daily-filterbar">
        <input
          className="form-input rrhh-daily-filter-search"
          type="search"
          value={query}
          onChange={(event) => updateFilters({ query: event.target.value })}
          placeholder="Buscar por nombre, rol o referencia del plan..."
          aria-label="Buscar por nombre, rol o referencia del plan"
        />
        <select className="form-input" value={arrivalFilter} onChange={(event) => updateFilters({ arrivalFilter: event.target.value })} aria-label="Filtrar por estado de llegada">
          <option value="all">Llegada: todos</option>
          <option value="ok">Verde · hasta 15 min</option>
          <option value="warning">Amarillo · 15 a 30 min</option>
          <option value="critical">Rojo · más de 30 min</option>
        </select>
        <select className="form-input" value={marksFilter} onChange={(event) => updateFilters({ marksFilter: event.target.value })} aria-label="Filtrar por estado de marcas">
          <option value="all">Marcas: ver todo</option>
          <option value="without">Sin marcas</option>
          <option value="with">Con marcas</option>
        </select>
        <select className="form-input" value={differenceFilter} onChange={(event) => updateFilters({ differenceFilter: event.target.value })} aria-label="Filtrar por diferencia">
          <option value="all">Diferencia: todas</option>
          <option value="negative">Negativa</option>
          <option value="positive">Positiva</option>
        </select>
        <select className="form-input" value={sortMode} onChange={(event) => updateFilters({ sortMode: event.target.value })} aria-label="Ordenar jornadas">
          <option value="none">Orden: original</option>
          <option value="arrival">Llegada: temprano a tarde</option>
          <option value="hours">Horas: mayor saldo</option>
        </select>
        <label className="rrhh-daily-pending-filter">
          <input
            type="checkbox"
            checked={pendingOnly}
            onChange={(event) => updateFilters({ pendingOnly: event.target.checked })}
          />
          <span>Pendientes</span>
        </label>
        <span className="rrhh-daily-filter-count">{filteredJournals.length} de {journals.length} personas</span>
      </header>
      <div className="rrhh-daily-list">
        {filteredJournals.length ? filteredJournals.map((row) => {
          const rowId = `${row.turno_id}|${row.persona_id}|${row.fecha}`;
          const segments = row.tramos || [];
          const firstEntryIndex = segments.findIndex((segment) => segment.entrada_hora);
          let finalExitIndex = -1;
          segments.forEach((segment, index) => { if (segment.salida_hora) finalExitIndex = index; });
          const hasComparablePlan = String(row.estado_turno).toUpperCase() === 'NORMAL' && row.hora_inicio && row.hora_fin;
          const differenceMinutes = hasComparablePlan ? journalDifferenceMinutes(row) : null;
          const hasMarks = (row.marcas_ids || []).length > 0
            || segments.some((segment) => segment.entrada_hora || segment.salida_hora);
          const hasTurn = Number.isInteger(Number(row.turno_id)) && Number(row.turno_id) > 0;
          const isFreeTurn = String(row.estado_turno || '').toUpperCase() === 'LIBRE';
          const canMarkWithoutMarks = hasTurn && !hasMarks;
          return <article className={`rrhh-daily-person${canEdit ? '' : ' no-actions'}`} key={`${row.persona_id}-${row.fecha}`}>
          <header className="rrhh-daily-person-head">
            <div><strong>{personName(row)}</strong><span>{row.rol_operativo || 'Sin rol operativo'}</span></div>
            <div>{stateBadge(row.estado_aprobacion)}</div>
          </header>
          <div className="rrhh-daily-plan">
            <small>Plan semanal</small>
            <strong><PlannedSchedule turn={row} /></strong>
            <span>{row.actividad_ubicacion || row.estado_turno}</span>
            <div className="rrhh-daily-hours-summary">
              <span><small>Previsto</small><strong>{formatHours(row.horas_previstas)} h</strong></span>
              <span><small>Real</small><strong>{formatHours(row.horas_trabajadas)} h</strong></span>
              {differenceMinutes === null
                ? <em>Sin comparación</em>
                : <em className={differenceMinutes === 0 ? 'is-even' : differenceMinutes > 0 ? 'is-over' : 'is-under'}>DIFERENCIA: {formatDifferenceMinutes(differenceMinutes)}</em>}
            </div>
          </div>
          <div className="rrhh-daily-segments-scroll">
            <div className="rrhh-daily-segments-track">
              {segments.length ? segments.map((segment, index) => {
              const entryDifference = markDifference(row, segment.entrada_fecha, segment.entrada_hora, 'entrada', index === firstEntryIndex);
              const exitDifference = markDifference(row, segment.salida_fecha, segment.salida_hora, 'salida', index === finalExitIndex);
              return <div className="rrhh-daily-segment" key={`${segment.entrada_id || 'x'}-${segment.salida_id || index}`}>
              <div className="rrhh-daily-segment-label">Tramo {String(index + 1).padStart(2, '0')}</div>
              <div className={`rrhh-daily-mark rrhh-daily-mark--${entryDifference.tone}`}>
                <small>Entrada</small><strong>{segment.entrada_hora || '—'}</strong>
                {locationValue(segment.entrada_ubicacion, segment.entrada_latitud, segment.entrada_longitud)}
                <em>{entryDifference.label}</em>
              </div>
              <div className={`rrhh-daily-mark rrhh-daily-mark--${exitDifference.tone}`}>
                <small>Salida</small><strong>{segment.salida_hora || 'Abierto'}</strong>
                {locationValue(segment.salida_ubicacion, segment.salida_latitud, segment.salida_longitud)}
                <em>{segment.salida_hora ? exitDifference.label : 'Tramo abierto'}</em>
              </div>
            </div>;
              }) : <div className="rrhh-daily-no-marks">Sin marcas registradas</div>}
            </div>
          </div>
          {canEdit && <aside className="rrhh-daily-actions">
            <button className="btn btn-sm rrhh-daily-action-wide rrhh-daily-action-approve" type="button" disabled={Boolean(savingKey) || !hasMarks} onClick={() => onJournalAction(row, 'APROBAR')}>Aprobar jornada</button>
            <button className="btn btn-sm rrhh-daily-action-wide rrhh-daily-action-plan" type="button" disabled={Boolean(savingKey) || !hasMarks} onClick={() => onJournalAction(row, 'APROBAR_Y_PLAN')}>Aprobar y pasar al plan</button>
            <button className="btn btn-sm rrhh-daily-action-compact rrhh-daily-action-edit" type="button" disabled={Boolean(savingKey)} onClick={() => onEdit(row)}>Modificar marcas</button>
            <button className="btn btn-sm rrhh-daily-action-compact rrhh-daily-action-absent" type="button" disabled={Boolean(savingKey) || !canMarkWithoutMarks} onClick={() => onJournalAction(row, 'AUSENTE')}>Marcar ausente</button>
            <button className="btn btn-sm rrhh-daily-action-compact rrhh-daily-action-free" type="button" disabled={Boolean(savingKey) || !canMarkWithoutMarks || isFreeTurn} onClick={() => onJournalAction(row, 'LIBRE')}>Marcar libre</button>
            {savingKey?.startsWith(`${rowId}|`) && <span className="text-sm text-dim">Guardando...</span>}
          </aside>}
        </article>;
        }) : <div className="rrhh-native-empty">{journals.length ? 'No hay personas que coincidan con los filtros.' : 'Sin turnos, marcas ni tramos para este día.'}</div>}
      </div>
    </section>
  </>;
}

function reportRows(snapshot, type) {
  const journals = snapshot.journals || [];
  if (type === 'marcas') return (snapshot.marks || []).map((row) => ({ Persona: personName(row), Fecha: row.fecha, Tipo: row.tipo, Hora: row.hora, Registro: row.tipo_marca, 'Estado validación': row.estado_aprobacion, Actividad: row.actividad_ubicacion || '', Ubicación: row.ubicacion_detectada || '' }));
  if (type === 'operaciones') return (snapshot.operations || []).map((row) => ({ Persona: personName(row), Fecha: dateFromOperation(row), Operación: row.tipo_operacion, Franja: row.franja, Referencia: row.referencia || '', Valor: row.valor || 0, Estado: row.estado, Comentario: row.validation_comment || row.motivo_rechazo || row.observacion || '' }));
  if (type === 'observaciones') return (snapshot.observations || []).map((row) => ({ Persona: personName(row), Fecha: row.fecha, Tipo: row.tipo, Severidad: row.severidad, Detalle: row.detalle || '', Estado: row.resuelta ? 'Resuelta' : row.estado, Origen: row.origen }));
  if (type === 'documentacion') return (snapshot.personnel || []).map((row) => ({ Persona: personName(row), Rol: row.operational_role || '', 'Libreta de conducir': row.driver_license_type || 'NO TIENE', 'Vence libreta': String(row.driver_license_expires_on || ''), 'Vence carné': String(row.health_card_expires_on || '') }));
  if (type === 'control') return journals.map((row) => ({ Persona: personName(row), Fecha: row.fecha, 'Horario previsto': plannedSchedule(row), Actividad: row.actividad_ubicacion || row.estado_turno, Entrada: row.entrada_hora || '', Salida: row.salida_hora || '', 'Horas reales': row.horas_trabajadas, Estado: row.estado_aprobacion }));
  return journals.map((row) => ({ Persona: personName(row), Rol: row.rol_operativo || '', Fecha: row.fecha, Previsión: plannedDetails(row), Entrada: row.entrada_hora || '', Salida: row.salida_hora || '', 'Horas previstas': row.horas_previstas, 'Horas reales': row.horas_trabajadas, Estado: row.estado_aprobacion }));
}

function Reports({ snapshot, type, setType, selectedMonth }) {
  const rows = useMemo(() => reportRows(snapshot, type), [snapshot, type]);
  const columns = Object.keys(rows[0] || {});
  return <>
    <section className="rrhh-native-card rrhh-insight-toolbar">
      <label>Tipo de reporte<select className="form-input" value={type} onChange={(event) => setType(event.target.value)}>{Object.entries(REPORT_TYPES).map(([id, label]) => <option key={id} value={id}>{label}</option>)}</select></label>
      <span className="text-sm text-dim">{rows.length} filas · fuente canónica</span>
      <button className="btn btn-primary" type="button" onClick={() => downloadCsv(rows, `rrhh-${type}-${selectedMonth}.csv`)} disabled={!rows.length}>Exportar CSV</button>
    </section>
    <section className="rrhh-native-card rrhh-validation-table-wrap">
      <table className="rrhh-native-table"><thead><tr>{columns.map((column) => <th key={column}>{column}</th>)}</tr></thead><tbody>
        {!rows.length && <tr><td colSpan={Math.max(columns.length, 1)}>Sin datos para el período seleccionado.</td></tr>}
        {rows.slice(0, 300).map((row, index) => <tr key={`${index}-${row.Persona || ''}`}>{columns.map((column) => <td key={column}>{row[column] ?? '—'}</td>)}</tr>)}
      </tbody></table>
    </section>
  </>;
}

function buildAnalysisRows(snapshot, period) {
  const days = inclusiveDays(period.from, period.to);
  const weeklyReference = 44;
  return (snapshot.personnel || []).map((person) => {
    const journals = (snapshot.journals || []).filter((row) => Number(row.persona_id) === Number(person.id));
    const approvedJournals = journals.filter((row) => approved(row.estado_aprobacion));
    const operations = (snapshot.operations || []).filter((row) => Number(row.persona_id) === Number(person.id) && normalizeStatus(row.estado) === 'APROBADA');
    const plannedHours = journals.reduce((sum, row) => sum + Number(row.horas_previstas || 0), 0);
    const workedHours = approvedJournals.reduce((sum, row) => sum + Number(row.horas_trabajadas || 0), 0);
    const extraHours = approvedJournals.reduce((sum, row) => sum + Math.max(0, Number(row.horas_trabajadas || 0) - 8), 0);
    const lateArrivals = approvedJournals.filter((row) => {
      const first = (row.tramos || []).find((segment) => segment.entrada_hora);
      return first && Number(markDifference(row, first.entrada_fecha, first.entrada_hora, 'entrada', true).minutes || 0) > 15;
    }).length;
    const earlyExits = approvedJournals.filter((row) => {
      let final = null;
      (row.tramos || []).forEach((segment) => { if (segment.salida_hora) final = segment; });
      return final && Number(markDifference(row, final.salida_fecha, final.salida_hora, 'salida', true).minutes || 0) < -15;
    }).length;
    const rests = journals.filter((row) => normalizeStatus(row.estado_turno) === 'LIBRE').length;
    const contractWeeklyHours = Number(person.agreed_hours || 0) || weeklyReference;
    const contractHours = (contractWeeklyHours / 7) * days;
    const hoursCost = approvedJournals.reduce((sum, row) => (
      sum + Number(row.horas_trabajadas || 0) * hourlyRateForDate(snapshot, person.id, row.fecha, person.hourly_rate)
    ), 0);
    const operationValue = operations.reduce((sum, row) => sum + Number(row.valor || 0), 0);
    return {
      id: person.id,
      persona: fullPersonName(person),
      rol: person.operational_role || '-',
      planificado: plannedHours,
      horas: workedHours,
      diferencia: workedHours - plannedHours,
      extras: extraHours,
      contrato: contractHours,
      descansos: rests,
      llegadasTarde: lateArrivals,
      salidasAnticipadas: earlyExits,
      operaciones: operations.length,
      costoHoras: hoursCost,
      costoOperaciones: operationValue,
      costoTotal: hoursCost + operationValue,
      tieneJornales: journals.length > 0,
    };
  }).filter((row) => row.tieneJornales || row.operaciones > 0);
}

function CompactRanking({ title, rows, value, empty = 'Sin datos para este corte.' }) {
  return <section className="rrhh-native-card rrhh-analysis-ranking">
    <h3>{title}</h3>
    <div>
      {rows.length ? rows.map((row, index) => <article key={`${title}-${row.id}`}>
        <span>{String(index + 1).padStart(2, '0')}</span>
        <strong>{row.persona}</strong>
        <em>{value(row)}</em>
      </article>) : <p>{empty}</p>}
    </div>
  </section>;
}

function Analysis({ snapshot, comparisonSnapshot, period }) {
  const [sortConfig, setSortConfig] = useState({ key: 'horas', direction: 'desc' });
  const [restDetail, setRestDetail] = useState('');
  const months = useMemo(() => analysisMonths(period.from, period.to), [period.from, period.to]);
  const restToday = snapshot.rest_today || currentIsoDate();
  const restMatrix = useMemo(() => buildRestMatrix(snapshot.rest_journals || [], months, restToday), [snapshot.rest_journals, months, restToday]);
  const emptyMonths = useMemo(() => Object.fromEntries(months.map((month) => [month.key, emptyRestMonth(month, restToday)])), [months, restToday]);
  const personMonth = (row, month) => restMatrix.get(Number(row.id))?.[month.key] || emptyMonths[month.key];
  useEffect(() => { setRestDetail(''); }, [period.from, period.to, snapshot.rest_journals]);
  const rows = useMemo(() => buildAnalysisRows(snapshot, period), [snapshot, period]);
  const comparisonRows = useMemo(() => buildAnalysisRows(comparisonSnapshot || { personnel: [], journals: [], operations: [], hourly_rates: [] }, { from: previousYearDate(period.from), to: previousYearDate(period.to) }), [comparisonSnapshot, period]);
  const activePeople = rows.filter((row) => row.tieneJornales).length;
  const totalPlannedHours = rows.reduce((sum, row) => sum + row.planificado, 0);
  const totalHours = rows.reduce((sum, row) => sum + row.horas, 0);
  const totalDifference = totalHours - totalPlannedHours;
  const totalExtras = rows.reduce((sum, row) => sum + row.extras, 0);
  const totalContract = rows.reduce((sum, row) => sum + row.contrato, 0);
  const totalOperations = rows.reduce((sum, row) => sum + row.operaciones, 0);
  const totalCost = rows.reduce((sum, row) => sum + row.costoTotal, 0);
  const previousCost = comparisonRows.reduce((sum, row) => sum + row.costoTotal, 0);
  const previousHours = comparisonRows.reduce((sum, row) => sum + row.horas, 0);
  const averageHours = activePeople ? totalHours / activePeople : 0;
  const sortedByHours = [...rows].sort((a, b) => b.horas - a.horas || a.persona.localeCompare(b.persona, 'es'));
  const lowLoad = [...rows].filter((row) => row.tieneJornales).sort((a, b) => a.horas - b.horas || a.persona.localeCompare(b.persona, 'es'));
  const lateRows = [...rows].filter((row) => row.llegadasTarde || row.salidasAnticipadas).sort((a, b) => (b.llegadasTarde + b.salidasAnticipadas) - (a.llegadasTarde + a.salidasAnticipadas));
  const operationRows = [...rows].filter((row) => row.operaciones > 0).sort((a, b) => b.operaciones - a.operaciones || a.persona.localeCompare(b.persona, 'es'));
  const redistributionRows = [...rows].filter((row) => row.tieneJornales && row.operaciones === 0).sort((a, b) => a.horas - b.horas).slice(0, 6);
  const byRole = rows.reduce((map, row) => {
    const key = row.rol || 'Sin rol';
    const current = map.get(key) || { rol: key, horas: 0, personas: 0 };
    current.horas += row.horas;
    current.personas += row.tieneJornales ? 1 : 0;
    map.set(key, current);
    return map;
  }, new Map());
  const roleRows = [...byRole.values()].sort((a, b) => b.horas - a.horas);
  const tableColumns = [
    { key: 'persona', label: 'Persona', value: (row) => row.persona },
    { key: 'planificado', label: 'Plan', value: (row) => row.planificado },
    { key: 'horas', label: 'Real', value: (row) => row.horas },
    { key: 'diferencia', label: 'Diferencia', value: (row) => row.diferencia },
    { key: 'extras', label: 'Extras +8', value: (row) => row.extras },
    { key: 'contrato', label: 'Contrato', value: (row) => row.contrato ? row.horas / row.contrato : 0 },
    { key: 'operaciones', label: 'Operaciones', value: (row) => row.operaciones },
    { key: 'costoTotal', label: 'Costo estimado', value: (row) => row.costoTotal },
  ];
  const restTotals = [
    { key: 'expected', label: 'Corresponde' },
    { key: 'taken', label: 'Descanso' },
    { key: 'pending', label: 'Pendiente' },
  ];
  const sortColumns = [...tableColumns, ...months.flatMap((month) => restTotals.map((total) => ({
    key: `rest-${month.key}-${total.key}`,
    value: (row) => personMonth(row, month)[total.key],
  })))];
  const sortColumn = sortColumns.find((item) => item.key === sortConfig.key) || tableColumns.find((item) => item.key === 'horas');
  const sortedTableRows = [...rows].sort((left, right) => (
      compareValues(sortColumn.value(left), sortColumn.value(right), sortConfig.direction)
      || left.persona.localeCompare(right.persona, 'es')
  ));
  const toggleSort = (key) => setSortConfig((current) => (
    current.key === key
      ? { key, direction: current.direction === 'desc' ? 'asc' : 'desc' }
      : { key, direction: 'desc' }
  ));
  return <>
    <section className="rrhh-analysis-hero rrhh-native-card">
      <div>
        <small>LANG / Análisis de RRHH</small>
        <h2>{periodLabel(period.from, period.to)}</h2>
        <p>Horas validadas, plan semanal, operaciones y costos estimados desde datos canónicos.</p>
      </div>
      <div className="rrhh-analysis-hero-badges">
        <span>{activePeople} personas con jornales</span>
        <span>{inclusiveDays(period.from, period.to)} días</span>
        <span>{formatPercentDelta(totalHours, previousHours)}</span>
      </div>
    </section>

    <section className="rrhh-native-grid rrhh-analysis-metrics">
      {metric('Horas trabajadas', `${formatHours(totalHours)} h`, `vs. ${formatHours(totalPlannedHours)} h planificadas`)}
      {metric('Diferencia vs. plan', `${formatSignedHours(totalDifference)} h`, totalDifference >= 0 ? 'Por encima del plan' : 'Por debajo del plan')}
      {metric('Extras sobre 8 h/día', `${formatHours(totalExtras)} h`, 'Suma diaria validada por persona')}
      {metric('Promedio por empleado', `${formatHours(averageHours)} h`, `${percent(totalHours, totalContract)} del contrato del período`)}
      {metric('Operaciones aprobadas', totalOperations, `${operationRows.length} personas con operaciones`)}
      {metric('Costo estimado', formatMoney(totalCost), formatPercentDelta(totalCost, previousCost))}
    </section>

    <section className="rrhh-analysis-split">
      <section className="rrhh-native-card rrhh-analysis-role-card">
        <h3>Horas por equipo</h3>
        <div className="rrhh-analysis-bars">
          {roleRows.map((row) => <article key={row.rol}>
            <div><strong>{row.rol}</strong><span>{formatHours(row.horas)} h · {row.personas} pers.</span></div>
            <em style={{ width: `${Math.max(8, Math.min(100, totalHours ? (row.horas / totalHours) * 100 : 0))}%` }} />
          </article>)}
        </div>
      </section>
      <section className="rrhh-native-card rrhh-analysis-alert-card">
        <h3>Lecturas rápidas</h3>
        <p><strong>Carga alta:</strong> {sortedByHours.slice(0, 3).map((row) => row.persona).join(', ') || 'sin datos'}.</p>
        <p><strong>Menor carga:</strong> {lowLoad.slice(0, 3).map((row) => row.persona).join(', ') || 'sin datos'}.</p>
        <p><strong>Llegadas/salidas:</strong> {lateRows.length ? `${lateRows.length} personas con desvíos relevantes.` : 'Sin desvíos relevantes.'}</p>
        <p><strong>Redistribución:</strong> {redistributionRows.length ? `${redistributionRows.map((row) => row.persona).join(', ')} podrían absorber operaciones.` : 'Sin oportunidades claras en este corte.'}</p>
      </section>
    </section>

    <section className="rrhh-analysis-rankings">
      <CompactRanking title="Mayor carga" rows={sortedByHours.slice(0, 8)} value={(row) => `${formatHours(row.horas)} h`} />
      <CompactRanking title="Menor carga" rows={lowLoad.slice(0, 8)} value={(row) => `${formatHours(row.horas)} h`} />
      <CompactRanking title="Llegadas tarde / salidas anticipadas" rows={lateRows.slice(0, 8)} value={(row) => `${row.llegadasTarde} / ${row.salidasAnticipadas}`} />
      <CompactRanking title="Operaciones por persona" rows={operationRows.slice(0, 8)} value={(row) => `${row.operaciones} ops.`} />
    </section>

    <div className="rrhh-analysis-rest-legend">
      <span><i className="rrhh-rest-work" />6 h o más</span>
      <span><i className="rrhh-rest-short" />Menos de 6 h · ½ día</span>
      <span><i className="rrhh-rest-free" />Libre · 1 día</span>
      <span><i className="rrhh-rest-unknown" />Sin confirmar / en curso / futuro</span>
      <span>Saldo mensual hasta ayer · sin arrastre</span>
    </div>
    {restDetail && <div className="rrhh-analysis-rest-detail" role="status">{restDetail}</div>}
    <section className="rrhh-native-card rrhh-validation-table-wrap rrhh-analysis-calendar-wrap" tabIndex={0} aria-label="Análisis por persona y calendario mensual de descansos">
      <table className="rrhh-native-table rrhh-analysis-table rrhh-analysis-calendar"><thead>
        <tr className="rrhh-analysis-month-head">
          <th colSpan={tableColumns.length}>Período seleccionado</th>
          {months.map((month) => <th key={month.key} colSpan={month.days.length + restTotals.length}>{month.label}</th>)}
        </tr>
        <tr>{tableColumns.map((column, index) => <th key={column.key} className={index === 0 ? 'rrhh-analysis-person-fixed' : ''} aria-sort={sortConfig.key === column.key ? (sortConfig.direction === 'desc' ? 'descending' : 'ascending') : 'none'}>
        <button className={`rrhh-analysis-sort-button${sortConfig.key === column.key ? ' active' : ''}`} type="button" onClick={() => toggleSort(column.key)}>
          <span>{column.label}</span>
          <em>{sortConfig.key === column.key ? (sortConfig.direction === 'desc' ? '↓' : '↑') : '↕'}</em>
        </button>
      </th>)}
        {months.map((month) => <React.Fragment key={month.key}>
          {month.days.map((day, index) => <th key={day.date} scope="col" className={`rrhh-analysis-day-head${day.monday || index === 0 ? ' rrhh-analysis-week-start' : ''}${day.date === restToday ? ' rrhh-analysis-today' : ''}`}>
            <span>{day.weekday}</span><strong>{day.number}</strong>
          </th>)}
          {restTotals.map((total) => {
            const key = `rest-${month.key}-${total.key}`;
            return <th key={key} className="rrhh-analysis-rest-total" aria-sort={sortConfig.key === key ? (sortConfig.direction === 'desc' ? 'descending' : 'ascending') : 'none'}>
              <button className="rrhh-analysis-sort-button" type="button" onClick={() => toggleSort(key)}>
                <span>{total.label}</span><em>{sortConfig.key === key ? (sortConfig.direction === 'desc' ? '↓' : '↑') : '↕'}</em>
              </button>
            </th>;
          })}
        </React.Fragment>)}
      </tr></thead><tbody>
        {sortedTableRows.map((row) => <tr key={row.id}>
          <td className="rrhh-analysis-person-fixed"><strong>{row.persona}</strong></td><td>{formatHours(row.planificado)} h</td><td>{formatHours(row.horas)} h</td><td>{formatSignedHours(row.diferencia)} h</td><td>{formatHours(row.extras)} h</td><td>{percent(row.horas, row.contrato)}</td><td>{row.operaciones}</td><td>{formatMoney(row.costoTotal)}</td>
          {months.map((month) => {
            const summary = personMonth(row, month);
            return <React.Fragment key={month.key}>
              {month.days.map((day, index) => {
                const cell = summary.days[day.date];
                const label = `${row.persona} · ${day.date.split('-').reverse().join('/')} · ${cell.reason}${cell.hours ? ` · ${formatHours(cell.hours)} h` : ''} · Descanso: ${restDaysText(cell.credit)} d`;
                return <td key={day.date} className={`rrhh-analysis-day-cell${day.monday || index === 0 ? ' rrhh-analysis-week-start' : ''}`}>
                  <button type="button" className={`rrhh-analysis-rest-cell rrhh-rest-${cell.kind}`} title={label} aria-label={label} onClick={() => setRestDetail(label)}>
                    {cell.kind === 'free' ? 'L' : cell.kind === 'unknown' ? '—' : formatHours(cell.hours)}
                  </button>
                </td>;
              })}
              {restTotals.map((total) => <td key={total.key} className="rrhh-analysis-rest-total">
                <span>{restDaysText(summary[total.key])} d</span>
                {total.key === 'pending' && summary.unknown > 0 && <small title={`${summary.unknown} días sin información confirmada`}>Provisional · {summary.unknown} s/c</small>}
              </td>)}
            </React.Fragment>;
          })}
        </tr>)}
        {!sortedTableRows.length && <tr><td colSpan={tableColumns.length + months.reduce((sum, month) => sum + month.days.length + restTotals.length, 0)}>Sin personas con jornadas u operaciones en el período.</td></tr>}
      </tbody></table>
    </section>
  </>;
}

export default function InsightsPage({ kind }) {
  const { user } = useAuth();
  const canEditJournals = canWrite(user, 'rrhh.jornales');
  const today = currentIsoDate();
  const [selectedDay, setSelectedDay] = useState(today);
  const [selectedMonth, setSelectedMonth] = useState(monthKey());
  const [analysisRange, setAnalysisRange] = useState(() => analysisPresetRange('current-month', today));
  const [reportType, setReportType] = useState('nomina');
  const [snapshot, setSnapshot] = useState({ personnel: [], journals: [], marks: [], operations: [], observations: [], hourly_rates: [] });
  const [comparisonSnapshot, setComparisonSnapshot] = useState({ personnel: [], journals: [], marks: [], operations: [], observations: [], hourly_rates: [] });
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState('');
  const [editingRow, setEditingRow] = useState(null);
  const [savingJournalKey, setSavingJournalKey] = useState('');
  const period = kind === 'analisis' ? analysisRange : periodFor(kind, selectedDay, selectedMonth);
  const labels = { dashboard: ['Dashboard', 'Control operativo con turnos, marcas y operaciones canónicas.'], reportes: ['Reportes', 'Exportaciones generadas desde el contrato canónico de RRHH.'], analisis: ['Análisis', 'Horas, carga, operaciones y costos del período.'] };

  async function refresh() {
    setLoading(true); setError('');
    try {
      if (kind === 'analisis') {
        const previousFrom = previousYearDate(period.from);
        const previousTo = previousYearDate(period.to);
        const [current, previous] = await Promise.all([
          rrhhCoreGet(`/${kind}/datos?desde=${period.from}&hasta=${period.to}&rest_months=1`),
          rrhhCoreGet(`/${kind}/datos?desde=${previousFrom}&hasta=${previousTo}`),
        ]);
        setSnapshot(current);
        setComparisonSnapshot(previous);
      } else {
        setSnapshot(await rrhhCoreGet(`/${kind}/datos?desde=${period.from}&hasta=${period.to}`));
        setComparisonSnapshot({ personnel: [], journals: [], marks: [], operations: [], observations: [], hourly_rates: [] });
      }
      setHasLoaded(true);
    }
    catch (err) { setError(err.message || 'No se pudieron cargar los datos'); }
    finally { setLoading(false); }
  }
  useEffect(() => { refresh(); }, [kind, period.from, period.to]);

  async function applyJournalAction(row, action) {
    const confirmations = {
      AUSENTE: `¿Marcar ausente a ${personName(row)}? Las marcas existentes quedarán anuladas.`,
      LIBRE: `¿Marcar libre a ${personName(row)}? Las marcas existentes quedarán anuladas.`,
      APROBAR_Y_PLAN: `¿Reemplazar el horario previsto por las marcas reales de ${personName(row)} y aprobar la jornada?`,
    };
    if (confirmations[action] && !window.confirm(confirmations[action])) return;
    const key = `${row.turno_id}|${row.persona_id}|${row.fecha}|${action}`;
    setSavingJournalKey(key);
    setError('');
    try {
      await rrhhCorePost('/validacion/jornales/accion', {
        accion: action,
        turno_id: row.turno_id,
        persona_id: row.persona_id,
        fecha: row.fecha,
        marca_ids: row.marcas_ids || [],
      });
      await refresh();
    } catch (err) {
      setError(err.message || 'No se pudo actualizar la jornada');
    } finally {
      setSavingJournalKey('');
    }
  }

  const [title, subtitle] = labels[kind];
  const isDashboardToday = selectedDay === today;
  const actions = kind === 'dashboard'
    ? <div className="rrhh-dashboard-date-nav">
      <button className="btn btn-secondary rrhh-dashboard-date-step" type="button" onClick={() => setSelectedDay((value) => shiftIsoDate(value, -1))} aria-label="Día anterior">‹</button>
      <button className={`btn btn-secondary rrhh-dashboard-date-today${isDashboardToday ? ' is-current' : ''}`} type="button" onClick={() => setSelectedDay(today)}>Hoy</button>
      <button className="btn btn-secondary rrhh-dashboard-date-step" type="button" onClick={() => setSelectedDay((value) => shiftIsoDate(value, 1))} aria-label="Día siguiente">›</button>
      <label className="rrhh-dashboard-date-picker">
        <span className="rrhh-dashboard-date-display">{formatDashboardDateLabel(selectedDay)}</span>
        <input type="date" value={selectedDay} onChange={(event) => setSelectedDay(event.target.value)} aria-label="Fecha del dashboard" />
      </label>
    </div>
    : kind === 'analisis'
      ? <div className="rrhh-analysis-period-actions">
        <label>Inicio<input className="form-input" type="date" value={analysisRange.from} onChange={(event) => setAnalysisRange((range) => ({ ...range, from: event.target.value || range.from }))} /></label>
        <label>Fin<input className="form-input" type="date" value={analysisRange.to} onChange={(event) => setAnalysisRange((range) => ({ ...range, to: event.target.value || range.to }))} /></label>
        <button className="btn btn-secondary" type="button" onClick={() => setAnalysisRange(analysisPresetRange('previous-month', today))}>Mes anterior</button>
        <button className="btn btn-secondary" type="button" onClick={() => setAnalysisRange(analysisPresetRange('current-month', today))}>Mes actual</button>
        <button className="btn btn-secondary" type="button" onClick={() => setAnalysisRange(analysisPresetRange('previous-week', today))}>Semana anterior</button>
        <button className="btn btn-secondary" type="button" onClick={() => setAnalysisRange(analysisPresetRange('first-half', today))}>1ra quincena</button>
        <button className="btn btn-secondary" type="button" onClick={() => setAnalysisRange(analysisPresetRange('second-half', today))}>2da quincena</button>
      </div>
      : <div className="rrhh-native-toolbar-group rrhh-period-actions">
      <button className="btn btn-secondary" type="button" onClick={refresh} disabled={loading}>Actualizar</button>
      <input className="form-input rrhh-month-input" type="month" value={selectedMonth} onChange={(event) => setSelectedMonth(event.target.value)} />
    </div>;
  return <RRHHNativeShell title={title} subtitle={subtitle} actions={actions}>
    {error && <div className="alert alert-error">{error}</div>}
    {loading && !hasLoaded ? <div className="rrhh-native-card rrhh-native-empty">Cargando datos canónicos...</div> : <div className={`rrhh-insight-content${loading ? ' is-refreshing' : ''}`}>
      {loading && hasLoaded && <div className="rrhh-insight-refreshing">Actualizando datos...</div>}
      {kind === 'dashboard' ? <Dashboard snapshot={snapshot} canEdit={canEditJournals} onEdit={setEditingRow} onJournalAction={applyJournalAction} savingKey={savingJournalKey} /> : kind === 'reportes' ? <Reports snapshot={snapshot} type={reportType} setType={setReportType} selectedMonth={selectedMonth} /> : <Analysis snapshot={snapshot} comparisonSnapshot={comparisonSnapshot} period={period} />}
    </div>}
    {editingRow && <JournalMarksEditor row={editingRow} onClose={() => setEditingRow(null)} onSaved={refresh} />}
  </RRHHNativeShell>;
}
